<template>
  <div class="max-w-full" ref="page">
    <transition name="fade">
      <div
        v-show="!(componentsLoaded === loadingStatus.length)"
        class="h-screen p-4 w-full content-center flex flex-wrap justify-center"
      >
        <div class="text-2xl text-blue-600 text-center container">
          <div v-if="showHeading" class="text-center text-oswald pb-10">
            <span class="text-6xl text-primary">market</span
            ><span class="text-6xl text-secondary">analyzer</span>
          </div>
          <div class="h-6 bg-gray-500 w-full shadow-inner rounded">
            <div
              class="h-6 bg-primary shadow-inner rounded w-1/2"
              :style="'width:' + percentLoaded + '%; transition-duration: 1s'"
            ></div>
          </div>
          <div class="pt-10 text-sans font-bold text-secondary">
            {{ loadingMessage }}
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-show="componentsLoaded === loadingStatus.length" class="">
        <div v-if="showHeading" class="text-center text-oswald p-8">
          <router-link :to="{ name: 'home' }">
            <span class="text-6xl text-primary">market</span
            ><span class="text-6xl text-secondary">analyzer</span></router-link
          >
        </div>
        <div class="bg-gray-200 print:bg-white">
          <div class="text-center">
            <div class="flex flex-wrap items-center text-gray-700 text-sans">
              <div class="w-full text-left lg:w-1/3 px-10 py-4">
                <div class="text-primary text-2xl font-bold">
                  {{ place.name }}
                </div>

                <div class=" text-yellow-600 pb-4" v-if="place.rating">
                  <stars :rating="place.rating"></stars>
                </div>

                <div class="" v-if="place.website">
                  <span
                    class="mdi mdi-monitor-cellphone text-gray-600 text-xl mr-2"
                  ></span>
                  <a
                    class="text-gray-700 underline cursor-pointer font-bold"
                    target="_blank"
                    :href="place.website"
                    >{{ website }}</a
                  >
                </div>

                <div class="" v-if="place.formatted_phone_number">
                  <span
                    class="mdi mdi-phone-outline text-gray-600 text-xl mr-2"
                  ></span>
                  <a
                    class="text-gray-700 underline cursor-pointer font-bold"
                    :href="'tel:' + place.formatted_phone_number"
                    >{{ place.formatted_phone_number }}</a
                  >
                </div>

                <div class="" v-if="place.adr_address">
                  <span
                    class="mdi mdi-map-marker-outline text-gray-600 text-xl mr-2"
                  ></span>
                  <span
                    class="text-gray-700 font-bold"
                    v-html="place.adr_address"
                  ></span>
                </div>
              </div>
              <div class="w-full lg:w-1/3">
                <img
                  v-if="place.photos"
                  :src="place.photos[0].getUrl()"
                  class="object-cover w-full h-64"
                  alt=""
                />
              </div>
              <div class="w-full h-64 lg:w-1/3 print:hidden">
                <gmap-map
                  :center="center"
                  :zoom="zoom"
                  :style="{
                    width: '100%',
                    height: '100%',
                    'min-height': '200px'
                  }"
                >
                  <gmap-marker :position="center" />
                  <gmap-circle
                    :center="center"
                    :radius="Math.round(primaryRadius * 1609.34)"
                    :visible="true"
                    :options="{
                      fillColor: primaryColor,
                      fillOpacity: 0.2,
                      strokeOpacity: 0.2,
                      strokeWeight: 1,
                      strokeColor: '#ffffff'
                    }"
                  ></gmap-circle>
                  <gmap-circle
                    :center="center"
                    :radius="Math.round(secondaryRadius * 1609.34)"
                    :visible="true"
                    :options="{
                      fillColor: secondaryColor,
                      fillOpacity: 0.2,
                      strokeOpacity: 0.2,
                      strokeWeight: 1,
                      strokeColor: '#ffffff'
                    }"
                  ></gmap-circle>
                </gmap-map>
              </div>
            </div>
          </div>
        </div>

        <template v-if="centerLoaded">
          <div
            class="py-16 flex text-center container mx-auto text-sans font-bold"
          >
            <div class="w-1/2">
              <div class="text-4xl text-primary uppercase">
                Primary
              </div>
              <div class="text-lg text-gray-600 mt-12">
                Radius
              </div>
              <div class="text-3xl text-primary">
                {{ this.$route.query.primaryRadius }} miles
              </div>
              <div class="text-lg text-gray-600 mt-12">
                Users
              </div>
              <div class="text-3xl text-primary">
                <reach
                  :location="center"
                  :radius="primaryRadius"
                  @loaded="loadingStatus[0].loaded = true"
                ></reach>
              </div>
              <div class="text-lg text-gray-600 mt-12 print:pt-32">
                Genders
              </div>
              <div class="flex w-full justify-center">
                <genders
                  :location="center"
                  :radius="primaryRadius"
                  :color="primaryColor"
                  @loaded="loadingStatus[2].loaded = true"
                ></genders>
              </div>
            </div>

            <div class="w-1/2">
              <div
                class="text-4xl text-sans font-bold text-secondary uppercase"
              >
                Secondary
              </div>
              <div class="text-lg text-gray-600 mt-12">
                Radius
              </div>
              <div class="text-3xl text-secondary">
                {{ this.$route.query.secondaryRadius }} miles
              </div>
              <div class="text-lg text-gray-600 mt-12">
                Users
              </div>
              <div class="text-3xl text-secondary">
                <reach
                  :location="center"
                  :radius="secondaryRadius"
                  @loaded="loadingStatus[1].loaded = true"
                ></reach>
              </div>
              <div class="text-lg text-gray-600 mt-12 print:pt-32">
                Genders
              </div>
              <div class="flex w-full justify-center">
                <genders
                  :location="center"
                  :radius="secondaryRadius"
                  :color="secondaryColor"
                  @loaded="loadingStatus[3].loaded = true"
                ></genders>
              </div>
            </div>
          </div>

          <div class="py-16 px-4 bg-gray-200 print:bg-white">
            <div class="container mx-auto">
              <div
                class="text-3xl text-primary text-sans font-bold text-center"
              >
                Age Groups
              </div>
              <ages
                :location="center"
                :primaryRadius="primaryRadius"
                :secondaryRadius="secondaryRadius"
                @loaded="loadingStatus[4].loaded = true"
              ></ages>
            </div>
          </div>

          <!-- <div class="py-16 px-4 print:bg-white">
            <div class="container mx-auto">
              <div
                class="text-3xl text-primary text-sans font-bold text-center"
              >
                In Market Auto Shoppers
              </div>
              <div class="text-gray-500 text-sans text-center italic">
                Powered By Oracle Data - POLK/IHS Markit
              </div>
              <in-market-shoppers
                :location="center"
                :primaryRadius="primaryRadius"
                :secondaryRadius="secondaryRadius"
                @loaded="loadingStatus[5].loaded = true"
              ></in-market-shoppers>
            </div>
          </div> -->

          <!-- <div class="py-16 bg-gray-200 px-4 print:bg-white">
            <div class="container mx-auto">
              <div
                class="text-3xl text-primary text-sans font-bold text-center print:pt-32"
              >
                Credit
              </div>
              <div class="text-gray-500 text-sans text-center italic">
                Powered By Oracle Data - TransUnion
              </div>
              <credit
                :location="center"
                :primaryRadius="primaryRadius"
                :secondaryRadius="secondaryRadius"
                @loaded="loadingStatus[6].loaded = true"
              ></credit>
              <div class="shadow overflow-hidden sm:rounded-lg print:hidden">
                <div>
                  <dl>
                    <div
                      class="bg-gray-100 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                    >
                      <dt class="text-sm leading-5 font-medium text-gray-600">
                        Up & Coming
                      </dt>
                      <dd
                        class="mt-1 text-sm leading-5 text-gray-600 sm:mt-0 sm:col-span-2"
                      >
                        Relatively new to credit, propensity for entry level
                        credit products.
                      </dd>
                    </div>
                    <div
                      class="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                    >
                      <dt class="text-sm leading-5 font-medium text-gray-600">
                        Established
                      </dt>
                      <dd
                        class="mt-1 text-sm leading-5 text-gray-600 sm:mt-0 sm:col-span-2"
                      >
                        Stable History, spends moderately, propensity for
                        expanded credit (i.e. loans).
                      </dd>
                    </div>
                    <div
                      class="bg-gray-100 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                    >
                      <dt class="text-sm leading-5 font-medium text-gray-600">
                        Thriving
                      </dt>
                      <dd
                        class="mt-1 text-sm leading-5 text-gray-600 sm:mt-0 sm:col-span-2"
                      >
                        High access to credit and spending, High Spenders.
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div> -->

          <div class="py-16 px-4 print:hidden">
            <div class="container mx-auto flex items-center flex-wrap">
              <div class="lg:w-2/3 w-full text-gray-500">
                Want to take a look at a different location or make some
                adjustments to the primary or secondary markets?
              </div>
              <div class="lg:w-1/3 w-full flex justify-around">
                <router-link
                  :to="{ name: 'home' }"
                  class="m-4 p-2 rounded text-gray-100 shadow text-center uppercase font-medium text-lg bg-primary cursor-pointer hover:shadow-lg"
                >
                  <span class="mdi mdi-magnify"></span> New Search
                </router-link>
                <button
                  class="m-4 p-2 rounded text-gray-100 shadow text-center uppercase font-medium text-lg bg-secondary cursor-pointer hover:shadow-lg"
                  @click="print()"
                >
                  <span class="mdi mdi-printer"></span> Print
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import genders from "../components/charts/Genders";
import ages from "../components/charts/Ages";
// import credit from "../components/charts/CreditTiers";
import reach from "../components/numbers/Reach";
// import inMarketShoppers from "../components/charts/InMarketShoppers";
import loadingMessages from "../funnies";
import gmapMap from "vue2-google-maps/src/components/map";
import gmapMarker from "vue2-google-maps/src/components/marker";
import gmapCircle from "vue2-google-maps/src/components/circle";
import cookies from "../cookies";
import stars from "../components/starRating";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("whiteLabel");

export default {
  components: {
    genders,
    ages,
    reach,
    // inMarketShoppers,
    // credit,
    gmapMap,
    gmapMarker,
    gmapCircle,
    stars
  },
  data() {
    return {
      place: {},
      placeId: "",
      primaryRadius: 0,
      secondaryRadius: 0,
      center: { lat: 45.508, lng: -73.587 },
      centerLoaded: false,
      zoom: 8,
      loadingStatus: [
        {
          name: "usersPrimary",
          loaded: false
        },
        {
          name: "usersSecondary",
          loaded: false
        },
        {
          name: "gendersPrimary",
          loaded: false
        },
        {
          name: "gendersSecondary",
          loaded: false
        },
        {
          name: "ages",
          loaded: false
        }
        // {
        //   name: "credit",
        //   loaded: false
        // },
        // {
        //   name: "inMarketShoppers",
        //   loaded: false
        // }
      ],
      loadingMessages: [],
      loadingMessage: null
    };
  },
  methods: {
    print() {
      window.print();
    },
    async loadPlace(placeId) {
      this.centerLoaded = false;
      const gmap = await this.$gmapApiPromiseLazy();
      try {
        const mapElem = document.createElement("div");
        const map = new google.maps.Map(mapElem);

        const request = {
          placeId: placeId
        };
        /* global google */
        const service = new google.maps.places.PlacesService(map);

        service.getDetails(request, (place, status) => {
          if (status !== gmap.maps.places.PlacesServiceStatus.OK) {
            throw "Place status is not okay";
          }
          this.place = place;

          this.center = {
            lat: this.place.geometry.location.lat(),
            lng: this.place.geometry.location.lng()
          };

          this.centerLoaded = true;

          this.saveRequest();
        });
      } catch (error) {
        console.error(error);
        this.error = "There was an error retrieving the place listing";
      }
    },
    getLoadingMessage() {
      this.loadingMessage = this.loadingMessages[
        Math.floor(Math.random() * (this.loadingMessages.length - 1))
      ];
    },
    setMapZoomLevel() {
      const zoomLevels = [
        {
          zoom: 10,
          min: 0,
          max: 5
        },
        {
          zoom: 9,
          min: 6,
          max: 15
        },
        {
          zoom: 8,
          min: 16,
          max: 30
        },
        {
          zoom: 7,
          min: 31,
          max: 50
        }
      ];

      zoomLevels.forEach(level => {
        const largestRadius = Math.max(
          this.primaryRadius,
          this.secondaryRadius
        );
        if (largestRadius <= level.max && largestRadius >= level.min) {
          this.zoom = level.zoom;
        }
      });
    },
    saveRequest() {
      if (process.env.VUE_APP_ENVIRONMENT === "production") {
        this.$http
          .post(
            "/reach-estimates/store-result",
            {
              /* eslint-disable */
              name: this.place.name,
              address: this.place.adr_address,
              secondary_radius: this.secondaryRadius,
              primary_radius: this.primaryRadius,
              google_place_id: this.placeId,
              user_id: parseInt(cookies.getCookie('_bb_user')) || null
              /* eslint-enable */
            },
            { retry: 3, retryDelay: 250 }
          )
          .then(() => {
            //console.log(response.data);
          })
          .catch(error => {
            console.log(error);
          });
      }
    }
  },
  computed: {
    ...mapGetters(["primaryColor", "secondaryColor", "showHeading"]),
    componentsLoaded() {
      return this.loadingStatus.filter(function(item) {
        return item.loaded;
      }).length;
    },
    percentLoaded() {
      return (
        Math.round((this.componentsLoaded / this.loadingStatus.length) * 100) ||
        0
      );
    },
    website() {
      if (!this.place.website) {
        return null;
      }
      const url = new URL(this.place.website);
      return url.hostname;
    }
  },
  created() {
    this.placeId = this.$route.query.placeId;
    this.primaryRadius = parseInt(this.$route.query.primaryRadius);
    this.secondaryRadius = parseInt(this.$route.query.secondaryRadius);
    this.loadPlace(this.placeId);
    this.loadingMessages = loadingMessages;
    this.getLoadingMessage();
    this.setMapZoomLevel();
    setInterval(() => {
      this.getLoadingMessage();
    }, 10000);
  }
};
</script>
